import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Select, message, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { m305Api, ReportItem } from '../../services/m305Api';
import './M305Form.css';

const { RangePicker } = DatePicker;

// ---- Filter state ----
interface FilterState {
  warehouseFrom: string;
  warehouseTo: string;
  productFrom: string;
  productTo: string;
  batchFrom: string;
  batchTo: string;
  dateFrom: string;
  dateTo: string;
  reportType: number;
}

const emptyFilter = (): FilterState => ({
  warehouseFrom: '',
  warehouseTo: '',
  productFrom: '',
  productTo: '',
  batchFrom: '',
  batchTo: '',
  dateFrom: '',
  dateTo: '',
  reportType: 1,
});

// ---- Table columns ----
const columns: ColumnsType<ReportItem> = [
  { title: '\u5eab\u4f4d', dataIndex: 'fm030_01', key: 'fm030_01', width: 70, fixed: 'left' },
  { title: '\u5eab\u4f4d\u540d\u7a31', dataIndex: 'warehouse_name', key: 'warehouse_name', width: 100, ellipsis: true },
  { title: '\u7522\u54c1\u4ee3\u78bc', dataIndex: 'fm030_02', key: 'fm030_02', width: 110 },
  { title: '\u6279\u865f', dataIndex: 'fm030_03', key: 'fm030_03', width: 110 },
  { title: '\u54c1\u540d1', dataIndex: 'fm030_21', key: 'fm030_21', width: 120, ellipsis: true },
  { title: '\u54c1\u540d2', dataIndex: 'fm030_22', key: 'fm030_22', width: 100, ellipsis: true },
  { title: '\u55ae\u4f4d', dataIndex: 'fm030_12', key: 'fm030_12', width: 55 },
  {
    title: '\u5165\u5eab\u65e5',
    dataIndex: 'fm030_13',
    key: 'fm030_13',
    width: 100,
    render: (v: string | null) => (v ? v.substring(0, 10) : ''),
  },
  {
    title: '\u5eab\u5b58\u91cf',
    dataIndex: 'fm030_16',
    key: 'fm030_16',
    width: 90,
    align: 'right',
    render: (v: number) => v.toFixed(2),
  },
  {
    title: '\u5eab\u5b58\u91d1\u984d',
    dataIndex: 'fm030_17',
    key: 'fm030_17',
    width: 100,
    align: 'right',
    render: (v: number) => v.toFixed(2),
  },
  {
    title: '\u5e73\u5747\u55ae\u50f9',
    dataIndex: 'fm030_18',
    key: 'fm030_18',
    width: 95,
    align: 'right',
    render: (v: number) => v.toFixed(3),
  },
  { title: '\u8272\u865f', dataIndex: 'fm030_51', key: 'fm030_51', width: 65 },
  { title: '\u8272\u865f\u540d\u7a31', dataIndex: 'color_name', key: 'color_name', width: 90, ellipsis: true },
  {
    title: '\u5e45\u5bec1',
    dataIndex: 'fm030_24',
    key: 'fm030_24',
    width: 70,
    align: 'right',
    render: (v: number) => (v ? v.toFixed(1) : ''),
  },
  {
    title: '\u5e45\u5bec2',
    dataIndex: 'fm030_25',
    key: 'fm030_25',
    width: 70,
    align: 'right',
    render: (v: number) => (v ? v.toFixed(1) : ''),
  },
  {
    title: '\u78bc\u91cd',
    dataIndex: 'fm030_26',
    key: 'fm030_26',
    width: 70,
    align: 'right',
    render: (v: number) => (v ? v.toFixed(2) : ''),
  },
  {
    title: '\u7c73\u5e73\u65b9\u91cd',
    dataIndex: 'fm030_27',
    key: 'fm030_27',
    width: 80,
    align: 'right',
    render: (v: number) => (v ? v.toFixed(2) : ''),
  },
  { title: '\u5339\u6578', dataIndex: 'fm030_57', key: 'fm030_57', width: 60 },
  { title: '\u5408\u7d04\u5ba2\u6236', dataIndex: 'contract_customer', key: 'contract_customer', width: 100, ellipsis: true },
];

// ---- Component ----
const M305Form: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterState>(emptyFilter());
  const [rows, setRows] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);

  const setField = useCallback(
    <K extends keyof FilterState>(field: K, value: FilterState[K]) => {
      setFilter((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await m305Api.getReport({
        warehouseFrom: filter.warehouseFrom,
        warehouseTo: filter.warehouseTo,
        productFrom: filter.productFrom,
        productTo: filter.productTo,
        batchFrom: filter.batchFrom,
        batchTo: filter.batchTo,
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
        reportType: filter.reportType,
      });
      setRows(res.items || []);
      setQueried(true);
      if (!res.items || res.items.length === 0) {
        message.info('No matching records found');
      } else {
        message.success(`Found ${res.total} records`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || 'Query failed');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onClear = useCallback(() => {
    setFilter(emptyFilter());
    setRows([]);
    setQueried(false);
  }, []);

  return (
    <div className="m305-form">
      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>Home</a>
              <span>/</span>
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>Inventory</a>
              <span>/</span>
              <span className="current">Order Batch Inventory Report</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>Order Batch Inventory Report</h1>
            <span className="badge">M305</span>
          </div>
        </div>

        <div className="filter-panel">
          <div className="filter-row">
            <span className="filter-label">Warehouse From:</span>
            <Input value={filter.warehouseFrom} onChange={(e) => setField('warehouseFrom', e.target.value)} onKeyDown={onKeyDown} placeholder="Start" style={{ width: 100 }} allowClear />
            <span className="filter-sep">To</span>
            <Input value={filter.warehouseTo} onChange={(e) => setField('warehouseTo', e.target.value)} onKeyDown={onKeyDown} placeholder="End" style={{ width: 100 }} allowClear />
            <span className="filter-label">Product From:</span>
            <Input value={filter.productFrom} onChange={(e) => setField('productFrom', e.target.value)} onKeyDown={onKeyDown} placeholder="Start" style={{ width: 130 }} allowClear />
            <span className="filter-sep">To</span>
            <Input value={filter.productTo} onChange={(e) => setField('productTo', e.target.value)} onKeyDown={onKeyDown} placeholder="End" style={{ width: 130 }} allowClear />
          </div>

          <div className="filter-row">
            <span className="filter-label">Batch From:</span>
            <Input value={filter.batchFrom} onChange={(e) => setField('batchFrom', e.target.value)} onKeyDown={onKeyDown} placeholder="Start" style={{ width: 130 }} allowClear />
            <span className="filter-sep">To</span>
            <Input value={filter.batchTo} onChange={(e) => setField('batchTo', e.target.value)} onKeyDown={onKeyDown} placeholder="End" style={{ width: 130 }} allowClear />
            <span className="filter-label">Report Type:</span>
            <Select value={filter.reportType} onChange={(v) => setField('reportType', v)} style={{ width: 160 }}>
              <Select.Option value={1}>By Warehouse</Select.Option>
              <Select.Option value={2}>By Customer</Select.Option>
              <Select.Option value={3}>By Batch No</Select.Option>
            </Select>
          </div>

          <div className="filter-row">
            <span className="filter-label">Entry Date:</span>
            <RangePicker
              value={filter.dateFrom && filter.dateTo ? [dayjs(filter.dateFrom), dayjs(filter.dateTo)] : null}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setField('dateFrom', dates[0].format('YYYY-MM-DD'));
                  setField('dateTo', dates[1].format('YYYY-MM-DD'));
                } else {
                  setField('dateFrom', '');
                  setField('dateTo', '');
                }
              }}
              placeholder={['Start Date', 'End Date']}
              style={{ width: 240 }}
            />
            <Button type="primary" onClick={onQuery} loading={loading}>Query</Button>
            <Button onClick={onClear}>Clear</Button>
          </div>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">Total: <strong>{rows.length}</strong> records</div>
            </div>
          )}
          {queried ? (
            <Table<ReportItem>
              columns={columns}
              dataSource={rows}
              rowKey={(r) => `${r.fm030_01}-${r.fm030_02}-${r.fm030_03}`}
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `Total ${t}` }}
              size="small"
              scroll={{ x: 1800, y: 460 }}
              locale={{ emptyText: 'No data' }}
              loading={loading}
            />
          ) : (
            <div className="empty-state">Set filter criteria and click "Query"</div>
          )}
        </div>

        <div className="action-bar">
          <Button danger size="large" onClick={() => navigate('/erp')}>Exit</Button>
        </div>
      </div>
    </div>
  );
};

export default M305Form;
